import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Box, Dialog, IconButton, Tooltip, Typography, styled } from "@mui/material";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import EmojiPicker from "emoji-picker-react";
import { apiGet, apiPost } from "src/configs/globalFunctions";
import useAuth from "src/hooks/useAuth";
import useSocket from "src/hooks/useSocket";
import "./SupportChat.css";

// =========================================
// Styled
// =========================================
const SupportButton = styled(IconButton)(({ theme }) => ({
  width: "auto",
  height: "auto",
  padding: theme.spacing(0.5),
}));

const UnreadBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#f44336",
    color: "#fff",
    fontSize: "0.65rem",
    minWidth: 16,
    height: 16,
    padding: "0 4px",
  },
}));

// =========================================
// Helpers
// =========================================
const STORAGE_KEY = "suporte_last_read";

const SUPORTE_PHONE_DEFAULT = "551148632226";

function getSupportePhone() {
  try {
    const cfg = JSON.parse(localStorage.getItem("app_config") || "{}");
    return cfg?.env?.suportePhone || SUPORTE_PHONE_DEFAULT;
  } catch {
    return SUPORTE_PHONE_DEFAULT;
  }
}

function getLastRead() {
  try {
    return parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
  } catch {
    return 0;
  }
}

function markAsRead() {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function compare(a, b) {
  if (a.updated_at < b.updated_at) return -1;
  if (a.updated_at > b.updated_at) return 1;
  return 0;
}

// =========================================
// Component
// =========================================
export default function SupportChat() {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const bodyRef = useRef(null);
  const openRef = useRef(false);
  const prevLenRef = useRef(0);

  const suportePhone = getSupportePhone();

  // Keep openRef in sync for use inside socket callbacks
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // ---- scroll helpers ----
  const scrollToBottom = useCallback((smooth = false) => {
    setTimeout(() => {
      const el = bodyRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    }, 30);
  }, []);

  const checkScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    setShowScrollBtn(!atBottom);
  }, []);

  // ---- fetch messages ----
  const fetchMessages = useCallback(() => {
    if (!suportePhone || !localStorage.getItem('accessToken')) return;
    setLoading(true);
    apiGet("/chat/messages/", {
      body: { phone: suportePhone },
      done: (resp) => {
        const sorted = (resp.data || []).sort(compare);
        setMessages(sorted);
        setLoading(false);

        if (!openRef.current) {
          const lastRead = getLastRead();
          const newCount = sorted.filter(
            (m) => !m.from_me && new Date(m.updated_at).getTime() > lastRead
          ).length;
          setUnread(newCount);
        }
      },
      error: () => setLoading(false),
    });
  }, [suportePhone]);

  // ---- open / close ----
  const handleOpen = () => {
    setOpen(true);
    setUnread(0);
    markAsRead();
    fetchMessages();
  };

  const handleClose = () => {
    setOpen(false);
    setEmojiOpen(false);
  };

  // ---- send ----
  const handleSend = async () => {
    const msg = text.trim();
    if (!msg || !suportePhone) return;

    const optimistic = {
      wa_id: `tmp_${Date.now()}`,
      type: "text",
      body: msg,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      contact: suportePhone,
      from_me: true,
    };

    setMessages((prev) => [...prev, optimistic]);
    setText("");
    setEmojiOpen(false);
    scrollToBottom(true);

    try {
      await apiPost("/v1/chat/sendMessage/", {
        body: { message: msg, destination: suportePhone },
      });
    } catch (err) {
      console.error("[SupportChat] send error:", err);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const handleKeyDown = (e) => {
    if (emojiOpen) e.stopPropagation();
    if (e.ctrlKey && e.key === "e") { e.preventDefault(); setEmojiOpen(true); }
    if (e.key === "Escape") { e.preventDefault(); setEmojiOpen(false); }
  };

  // ---- scroll when messages load or are added ----
  useEffect(() => {
    if (open && !loading) scrollToBottom();
  }, [open, loading]);

  useEffect(() => {
    if (messages.length > prevLenRef.current) {
      prevLenRef.current = messages.length;
      if (open) scrollToBottom(true);
    }
  }, [messages.length, open]);

  // ---- socket (mesmo padrão do contentChat) ----
  useEffect(() => {
    if (!socket || !user?.id) return;

    const roomId = user.rootId || user.id;
    const onConnect = () => {
      console.log("[SupportChat] socket URL:", socket.io?.uri);
      console.log("[SupportChat] join-room:", roomId);
      socket.emit("join-room", roomId);
    };

    const onWaSecure = (val) => {
      const norm = (p) => String(p || "").replace(/\D/g, "");
      const inbound = norm(val?.contact);
      const support = norm(suportePhone);
      console.log("[SupportChat] WaSecure contact:", val?.contact, "| support:", suportePhone);
      if (!inbound || !support) return;
      if (inbound.slice(-8) !== support.slice(-8)) return;

      if (openRef.current) {
        setMessages((prev) => [...prev, val]);
        markAsRead();
      } else {
        setUnread((prev) => prev + 1);
      }
    };

    socket.on("connect", onConnect);
    socket.on("WaSecure", onWaSecure);

    // Se o socket já estava conectado quando o effect rodou,
    // "connect" não vai disparar novamente — emitir join-room diretamente
    if (socket.connected) {
      console.log("[SupportChat] já conectado, join-room:", roomId);
      socket.emit("join-room", roomId);
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("WaSecure", onWaSecure);
    };
  }, [socket, user?.id, suportePhone]);

  // ---- initial unread on mount ----
  useEffect(() => {
    if (suportePhone) fetchMessages();
  }, [fetchMessages]);

  // =========================================
  // Render
  // =========================================
  if (!user) return null;

  return (
    <>
      <Tooltip arrow title="Suporte Técnico">
        <SupportButton color="primary" onClick={handleOpen} aria-label="suporte">
          <UnreadBadge badgeContent={unread} invisible={unread === 0}>
            <HeadsetMicIcon fontSize="medium" />
          </UnreadBadge>
        </SupportButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 700,
            height: 550,
            maxWidth: "95vw",
            maxHeight: "90vh",
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#141c23",
            m: 0,
          },
        }}
      >
        {/* ── Header ── */}
        <Box className="suporte-header">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HeadsetMicIcon sx={{ color: "#4ADF83", fontSize: 26 }} />
            <Box>
              <Typography sx={{ color: "#4ADF83", fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
                Suporte Técnico
              </Typography>
              <Typography sx={{ color: "#919191", fontSize: 11, lineHeight: 1 }}>
                WhatsTV
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: "#919191" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Body (flex:1, relative for scroll btn) ── */}
        <Box sx={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 0 }}>
          <Box
            className="suporte-body"
            ref={bodyRef}
            onScroll={checkScroll}
          >
            {!suportePhone ? (
              <Box className="suporte-state-box">
                <HeadsetMicIcon sx={{ fontSize: 44, color: "#444", mb: 1 }} />
                <Typography sx={{ color: "#919191", fontSize: 14 }}>
                  Suporte não configurado.
                </Typography>
                <Typography sx={{ color: "#555", fontSize: 12 }}>
                  Defina <code style={{ color: "#4ADF83" }}>env.suportePhone</code> no painel de configuração.
                </Typography>
              </Box>
            ) : loading && messages.length === 0 ? (
              <Box className="suporte-state-box">
                <Box className="suporte-dots">
                  <span /><span /><span />
                </Box>
                <Typography sx={{ color: "#919191", fontSize: 13 }}>
                  Carregando mensagens...
                </Typography>
              </Box>
            ) : messages.length === 0 ? (
              <Box className="suporte-state-box">
                <HeadsetMicIcon sx={{ fontSize: 40, color: "#333", mb: 1 }} />
                <Typography sx={{ color: "#919191", fontSize: 13, textAlign: "center" }}>
                  Nenhuma mensagem ainda.
                  <br />
                  Envie uma mensagem para iniciar o atendimento.
                </Typography>
              </Box>
            ) : (
              messages.map((msg, i) => (
                <Box
                  key={msg.wa_id || i}
                  className={`suporte-msg ${msg.from_me ? "suporte-msg--me" : "suporte-msg--them"}`}
                >
                  <Box className="suporte-bubble">
                    <Typography className="suporte-bubble-text">{msg.body}</Typography>
                    <Typography className="suporte-bubble-time">
                      {formatTime(msg.updated_at || msg.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Scroll to bottom */}
          <Box
            className={`suporte-scroll-btn ${showScrollBtn ? "visible" : ""}`}
            onClick={() => scrollToBottom(true)}
          >
            <KeyboardArrowDownIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
        </Box>

        {/* ── Emoji picker ── */}
        <Box className="suporte-emoji-area" style={{ height: emojiOpen ? "300px" : "0px" }}>
          <EmojiPicker
            onEmojiClick={(obj) => setText((p) => p + obj.emoji)}
            searchDisabled
            previewConfig={{ showPreview: false }}
          />
        </Box>

        {/* ── Footer ── */}
        {suportePhone && (
          <Box className="suporte-footer">
            <Box className="suporte-footer-inner">
              <Box
                className="suporte-icon-btn"
                onClick={() => setEmojiOpen((p) => !p)}
                sx={{ opacity: emojiOpen ? 1 : 0.6 }}
              >
                <InsertEmoticonIcon sx={{ color: "#919191", fontSize: 22 }} />
              </Box>

              <input
                className="suporte-input"
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyUp={handleKeyUp}
                onKeyDown={handleKeyDown}
                placeholder="Digite uma mensagem"
                autoFocus={open}
              />

              <Box className="suporte-send-btn" onClick={handleSend}>
                <SendIcon sx={{ color: "#fff", fontSize: "1.1rem" }} />
                <span>Enviar</span>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
}
